import React, { useState, useEffect } from 'react';
import { api } from '@services/api';
import {
  Container,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Switch,
  FormControlLabel
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { SettingsBackupRestore, CheckCircle, Block } from '@material-ui/icons';
import { type Color } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { ToastMessage } from '@components/ToastMessage';
import usePagination from '@hooks/usePagination';
import { FileX, MagnifyingGlass } from '@phosphor-icons/react';
import { Pagination } from '@material-ui/lab';
import { FilePdf } from '@phosphor-icons/react/dist/ssr';
import { AppError } from '@utils/AppError';
import { useAuth } from '@context/auth';
import { DialogActionDelete } from '../../../components/DialogAction';
import { type Contratos } from '@dtos/contratosDTO';
import { ContainerButtonsTop, DivSearch } from './styles';
import { maskDate } from '@utils/masks';
import { NavLink } from '@components/NavLink';
import { Button as MuiButton } from '@material-ui/core';

export interface IDialog {
  nome_contrato: string;
  cod_contrato: string;
  data_criacao: string | Date;
  data_renovacao: string | Date;
  arquivo: string[] | null;
  exame: object[];
  risco: string;
  servico: string;
  num_conv_med: string;
  num_conv_odont: string;
  num_conv_acade: string;
  num_seg_vida: string;
  num_apol_pat: string;
}

const ContratosInativos: React.FC = () => {
  const [contratos, setContratos] = useState<Contratos[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    cod: '',
    message: '',
    color: '' as Color,
    show: false
  });

  // Estados para os modais de confirmação
  const [confirmacaoContratoModal, setConfirmacaoContratoModal] = useState(false);
  const [contratoParaAtivar, setContratoParaAtivar] = useState<Contratos | null>(null);

  const [value, setValue] = useState('');
  const { user } = useAuth();

  const [page, setPage] = useState(1);
  const PER_PAGE = 30;

  const count = Math.ceil(contratos.length / PER_PAGE);
  const _DATA = usePagination(contratos, PER_PAGE);

  // Função para abrir modal de confirmação de ativação do contrato
  function abrirModalAtivarContrato(contrato: Contratos) {
    setContratoParaAtivar(contrato);
    setConfirmacaoContratoModal(true);
  }

  // Função para fechar modal de confirmação do contrato
  function fecharModalContrato() {
    setConfirmacaoContratoModal(false);
    setContratoParaAtivar(null);
  }

  // Função para ativar contrato
  async function ativarContrato() {
    if (!contratoParaAtivar) return;

    try {
      // Chamada para a rota de ativação do contrato
      await api.patch(`/ravim/contratos/status`, {
        cod_contrato: contratoParaAtivar.cod_contrato,
        exibir: 'sim'
      });

      // Remove o contrato da lista local (pois agora está ativo)
      setContratos(prevContratos =>
        prevContratos.filter(contrato => contrato.cod_contrato !== contratoParaAtivar.cod_contrato)
      );

      setToastMessage({
        cod: '',
        color: 'success',
        message: 'Contrato ativado com sucesso!',
        show: true
      });

    } catch (error) {
      console.error('Erro ao ativar contrato:', error);
      setToastMessage({
        cod: '',
        color: 'error',
        message: 'Erro ao ativar contrato',
        show: true
      });
    } finally {
      fecharModalContrato();
    }
  }

  // Função para verificar se o contrato está ativo
  const isContratoAtivo = (exibir: string): boolean => {
    if (!exibir) return false;
    return exibir.toLowerCase() === 'sim';
  };

  const filterContractsPerPage =
    _DATA.currentData().length > 0
      ? _DATA
          .currentData()
          .filter(
            (contrato: Contratos) =>
              contrato.nome_contrato
                .toString()
                .toUpperCase()
                .includes(search.toUpperCase()) ||
              contrato.cod_contrato.toString().toUpperCase().includes(search.toUpperCase())
          )
      : [];

  const filterContracts =
    contratos.length > 0
      ? contratos.filter(
          (contrato: Contratos) =>
            contrato.nome_contrato
              .toString()
              .toUpperCase()
              .includes(search.toUpperCase()) ||
            contrato.cod_contrato.toString().toUpperCase().includes(search.toUpperCase())
        )
      : [];

  const filter = search.length > 0 ? filterContracts : filterContractsPerPage;

async function fetchContratosInativos() {
  try {
    setLoading(false);
    const res = await api.get(`/ravim/contratos-empresas?status=inativo`);
    
    if (res.status === 404) return;
    
    // VERIFIQUE A ESTRUTURA DA RESPOSTA
    console.log('Resposta da API:', res.data);
    
    // Ajuste conforme a estrutura real da resposta
    if (res.data && Array.isArray(res.data.data)) {
      setContratos(res.data.data);  // Se a resposta for { data: [], ... }
    } else if (Array.isArray(res.data)) {
      setContratos(res.data);       // Se a resposta for direto o array
    } else {
      setContratos([]);             // Fallback para array vazio
    }
    
  } catch (error) {
    const messageError = error instanceof AppError;
    const message = messageError
      ? error.message
      : 'Não foi possível carregar lista de Contratos inativos.';
    setToastMessage({
      cod: '',
      color: 'error',
      message,
      show: true
    });
    setContratos([]); // Garante que seja array vazio em caso de erro
  } finally {
    setLoading(true);
  }
}

  const handleChange = (e: any, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    fetchContratosInativos();
  }, []);

  useEffect(() => {
    if (!value) {
      setSearch('');
    }
  }, [value]);

  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            label="Pesquisar contratos inativos"
            type="text"
            variant="outlined"
            style={{ background: '#FFF' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch(value);
              }
            }}
          />
          <Button
            variantstyle="SECUNDARY"
            icon={<MagnifyingGlass size={25} />}
            onClick={() => {
              handleSearch(value);
            }}
          />
        </DivSearch>
        {/* Remove o botão de cadastro se não for necessário para contratos inativos */}
        {/* Ou mantém se quiser permitir cadastro direto como inativo */}
        {/* <NavLink to="/cadastro-contrato-empresa">
          <Button title="Cadastrar contrato" />
        </NavLink> */}
      </ContainerButtonsTop>

      <Typography
        variant="h4"
        align="center"
        style={{ marginBottom: 20, fontWeight: 'bold' }}
      >
        Contratos Inativos
      </Typography>
      <Typography variant="h6" align="left">
        Total contratos inativos: {contratos.length}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20
        }}
      >
        <Pagination
          count={count}
          size="large"
          page={page}
          variant="outlined"
          shape="rounded"
          onChange={handleChange}
        />
        {loading ? (
          <TableContainer component={Paper}>
            <Table style={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nome do Contrato</TableCell>
                  <TableCell>Código do Contrato</TableCell>
                  <TableCell>Data de Criação</TableCell>
                  <TableCell>Data de Renovação</TableCell>
                  {/* <TableCell>Arquivo</TableCell>
                  <TableCell>ATUALIZAR</TableCell> */}
                  {/* REMOVIDA A COLUNA EMPRESAS */}
                  <TableCell>STATUS</TableCell> {/* MUDADO DE "ATIVAR" PARA "STATUS" */}
                </TableRow>
              </TableHead>
              <TableBody>
                {filter &&
                  filter.map((contrato: Contratos, index: number) => {
                    const estaAtivo = isContratoAtivo(contrato.exibir);
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>{contrato.nome_contrato}</TableCell>
                        <TableCell>{contrato.cod_contrato}</TableCell>
                        <TableCell>{maskDate(contrato.data_criacao)}</TableCell>
                        <TableCell>
                          {maskDate(contrato.data_renovacao)}
                        </TableCell>
                        {/* <TableCell>
                          {contrato.arquivo && contrato.arquivo.toString().toLowerCase() === 'true' ? (
                            <FilePdf size={30} />
                          ) : (
                            <FileX size={30} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Link
                            to={{
                              pathname: '/editar-contrato',
                              state: contrato
                            }}
                          >
                            <IconButton>
                              <SettingsBackupRestore
                                fontSize="medium"
                                style={{ color: '#422C59' }}
                              />
                            </IconButton>
                          </Link>
                        </TableCell> */}
                        {/* REMOVIDA A COLUNA EMPRESAS */}
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={estaAtivo}
                                onChange={() => {
                                  abrirModalAtivarContrato(contrato);
                                }}
                                name="status-contrato"
                                color="primary"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {estaAtivo ? 'Ativado' : 'Desativado'}
                              </Typography>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </div>
        )}
        <Pagination
          count={count}
          size="large"
          page={page}
          variant="outlined"
          shape="rounded"
          onChange={handleChange}
        />
      </div>

      {/* Modal de confirmação para ativar contrato */}
      <Dialog
        open={confirmacaoContratoModal}
        onClose={fecharModalContrato}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmar Ativação
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja ativar o contrato <strong>"{contratoParaAtivar?.nome_contrato}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
            Código: {contratoParaAtivar?.cod_contrato}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 10, fontWeight: 'bold', color: '#2e7d32' }}>
            O contrato ficará visível no sistema após a ativação.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={fecharModalContrato}
            variant="outlined" 
            color="primary"
          >
            Cancelar
          </MuiButton>
          <MuiButton 
            onClick={ativarContrato}
            variant="contained" 
            color="primary"
            startIcon={<CheckCircle />}
            style={{ backgroundColor: '#2e7d32', color: '#fff' }}
          >
            Ativar Contrato
          </MuiButton>
        </DialogActions>
      </Dialog>

      <ToastMessage
        code={toastMessage.cod}
        message={toastMessage.message}
        show={toastMessage.show}
        severity={toastMessage.color}
        onClose={() => {
          setToastMessage({
            ...toastMessage,
            show: false
          });
        }}
      />
    </Container>
  );
};

export default ContratosInativos;